import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  queueLocationPing,
  queuedLocationCount,
  queuedLocationPings,
  removeLocationPings,
} from "../services/driverLocationQueue";

const GPS_INTERVAL_MS = 5 * 60 * 1000;
const SYNC_INTERVAL_MS = 20000;

export default function useDriverGpsTracking({ detail, scopeKey, sendBatch }) {
  const [status, setStatus] = useState({
    active: false,
    label: "GPS tracking inactive",
    pendingCount: 0,
    lastSyncedAt: "",
    error: "",
    permission: "unknown",
  });
  const activeWindow = useMemo(() => gpsActiveWindow(detail), [detail]);
  const activeRef = useRef(activeWindow);
  const syncingRef = useRef(false);

  useEffect(() => {
    activeRef.current = activeWindow;
    setStatus((current) => ({
      ...current,
      active: activeWindow.active,
      label: activeWindow.active ? `GPS tracking active (${activeWindow.dayLabel})` : "GPS tracking paused",
      error: activeWindow.active ? current.error : "",
    }));
  }, [activeWindow]);

  const refreshPendingCount = useCallback(async () => {
    if (!scopeKey) return;
    try {
      const count = await queuedLocationCount(scopeKey);
      setStatus((current) => ({ ...current, pendingCount: count }));
    } catch {
      setStatus((current) => ({ ...current, error: "Could not read saved GPS queue" }));
    }
  }, [scopeKey]);

  const requestPermission = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus((current) => ({ ...current, permission: "unsupported", error: "GPS is not supported on this device/browser" }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => setStatus((current) => ({ ...current, permission: "granted", error: "" })),
      (error) => setStatus((current) => ({
        ...current,
        permission: error?.code === 1 ? "denied" : "unknown",
        error: geolocationError(error),
      })),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 },
    );
  }, []);

  const flush = useCallback(async () => {
    if (!scopeKey || !sendBatch || syncingRef.current) return;
    syncingRef.current = true;
    try {
      const queued = await queuedLocationPings(scopeKey);
      if (!queued.length) {
        setStatus((current) => ({ ...current, pendingCount: 0 }));
        return;
      }
      const batch = queued.slice(0, 100).map(({ scopeKey: _scopeKey, ...ping }) => ping);
      const response = await sendBatch(batch);
      await removeLocationPings(response?.acceptedClientPingIds || batch.map((ping) => ping.clientPingId));
      setStatus((current) => ({
        ...current,
        pendingCount: Math.max(0, queued.length - batch.length),
        lastSyncedAt: new Date().toISOString(),
        error: "",
      }));
    } catch {
      setStatus((current) => ({ ...current, error: "Offline - saving GPS locally" }));
    } finally {
      syncingRef.current = false;
    }
  }, [scopeKey, sendBatch]);

  const capture = useCallback(() => {
    const windowState = activeRef.current;
    if (!windowState.active || !scopeKey) return;
    if (!navigator.geolocation) {
      setStatus((current) => ({ ...current, error: "GPS is not supported on this device/browser" }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const capturedAt = new Date().toISOString();
        const ping = {
          clientPingId: `${scopeKey}:${capturedAt}:${Math.random().toString(36).slice(2, 10)}`,
          dayKey: windowState.dayKey,
          dayLabel: windowState.dayLabel,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          clientCapturedAt: capturedAt,
        };
        try {
          await queueLocationPing(scopeKey, ping);
          await refreshPendingCount();
          await flush();
        } catch {
          setStatus((current) => ({ ...current, error: "Could not save GPS on this device" }));
        }
      },
      (error) => {
        setStatus((current) => ({ ...current, error: geolocationError(error) }));
      },
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 20000 },
    );
  }, [flush, refreshPendingCount, scopeKey]);

  useEffect(() => {
    if (!scopeKey) return undefined;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((permission) => {
          setStatus((current) => ({ ...current, permission: permission.state || "unknown" }));
          permission.onchange = () => setStatus((current) => ({ ...current, permission: permission.state || "unknown" }));
        })
        .catch(() => {});
    }
    refreshPendingCount();
    window.addEventListener("online", flush);
    const syncInterval = setInterval(flush, SYNC_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", flush);
      clearInterval(syncInterval);
    };
  }, [flush, refreshPendingCount, scopeKey]);

  useEffect(() => {
    if (!activeWindow.active) return undefined;
    capture();
    const interval = setInterval(capture, GPS_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeWindow.active, activeWindow.dayKey, capture]);

  return { ...status, requestPermission };
}

function gpsActiveWindow(detail) {
  if (!detail?.tripStarted || detail?.tripEnded) return { active: false };
  const groups = groupPlacesByDay(detail.places || []);
  if (!groups.length) return { active: true, dayKey: "__TRIP__", dayLabel: "Trip" };
  const eventMap = buildEventMap(detail);
  for (let index = 0; index < groups.length; index += 1) {
    const day = groups[index];
    const started = index === 0 ? detail.tripStarted : eventMap.get(`DAY_START:${day.key}`);
    const ended = index === groups.length - 1 ? detail.tripEnded : eventMap.get(`DAY_END:${day.key}`);
    if (started && !ended) {
      return { active: true, dayKey: day.key, dayLabel: day.label || `Day ${index + 1}` };
    }
  }
  return { active: false };
}

function buildEventMap(detail) {
  const map = new Map();
  (detail?.events || []).forEach((event) => map.set(`${event.eventType}:${event.placeKey}`, event));
  return map;
}

function groupPlacesByDay(places) {
  const groups = [];
  const byKey = new Map();
  places.forEach((place) => {
    const key = place.dayKey || "day_0";
    if (!byKey.has(key)) {
      const group = { key, label: place.dayLabel || `Day ${groups.length + 1}`, places: [] };
      groups.push(group);
      byKey.set(key, group);
    }
    byKey.get(key).places.push(place);
  });
  return groups;
}

function geolocationError(error) {
  if (error?.code === 1) return "Allow Location";
  if (error?.code === 2) return "Turn ON Location";
  if (error?.code === 3) return "Try Again";
  return "GPS Failed";
}
